import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import Button from "../../../components/button/Button";
import Input from "../../../components/input/Input";
import Select from "react-select";
import styles from "./CreateBillingInvoiceScreen.module.scss";
import classNames from "classnames";
import {
  createBillingInvoice,
  createBillingInvoiceResetState,
} from "../../../store/actions/admin/billingInvoices";
import billingInvoicesService from "../../../apis/services/admin/billingInvoicesService";
import taxPayersService from "../../../apis/services/admin/taxPayersService";
import moment from "moment";

const BILLING_PERIOD_OPTIONS = [
  { value: "monthly", label: "Mesečno" },
  { value: "quarterly", label: "Kvartalno" },
  { value: "semi-annual", label: "Polugodišnje" },
  { value: "annual", label: "Godišnje" },
];

const CreateBillingInvoiceScreen = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const prefilledData = location.state?.prefilledData || null;
  
  const [invoiceSchemaId, setInvoiceSchemaId] = useState(null);
  const [billingCompanyId, setBillingCompanyId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [billingPeriodStart, setBillingPeriodStart] = useState("");
  const [billingPeriodEnd, setBillingPeriodEnd] = useState("");
  const [billingPeriodType, setBillingPeriodType] = useState({ value: "monthly", label: "Mesečno" });
  
  const [invoiceSchemas, setInvoiceSchemas] = useState([]);
  const [billingCompanies, setBillingCompanies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [loadingBillingCompanies, setLoadingBillingCompanies] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const createInvoiceInfo = useSelector((state) => state.billingInvoices.createInvoice);
  const hasPrefilledRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      // Load invoice schemas - try to load more if we have prefilledData
      setLoadingSchemas(true);
      try {
        const perPage = prefilledData?.invoiceSchemaId ? 100 : 20; // Load more if we need to find a specific schema
        const schemasResponse = await billingInvoicesService.getInvoiceSchemas(1, perPage);
        const schemasData = schemasResponse.data;
        if (!isMounted) return;
        
        // Handle different response structures
        let schemasArray = [];
        if (Array.isArray(schemasData)) {
          schemasArray = schemasData;
        } else if (schemasData && Array.isArray(schemasData.data)) {
          schemasArray = schemasData.data;
        } else if (schemasData && Array.isArray(schemasData.schemas)) {
          schemasArray = schemasData.schemas;
        }

        // If we have prefilledData and the schema is not in the list, try to fetch it directly
        if (prefilledData?.invoiceSchemaId && isMounted) {
          const schemaFound = schemasArray.find((s) => 
            s.id === prefilledData.invoiceSchemaId || 
            s.id === parseInt(prefilledData.invoiceSchemaId) ||
            s.id.toString() === prefilledData.invoiceSchemaId.toString()
          );
          
          if (!schemaFound) {
            try {
              const specificSchemaResponse = await billingInvoicesService.getInvoiceSchema(prefilledData.invoiceSchemaId);
              const specificSchema = specificSchemaResponse.data?.data || specificSchemaResponse.data || specificSchemaResponse;
              if (specificSchema && specificSchema.id) {
                schemasArray.push(specificSchema);
              }
            } catch (error) {
              console.error("Error loading specific invoice schema:", error);
            }
          }
        }

        if (isMounted) {
          setInvoiceSchemas(schemasArray);
        }
      } catch (error) {
        console.error("Error loading invoice schemas:", error);
        if (isMounted) setInvoiceSchemas([]);
      } finally {
        if (isMounted) setLoadingSchemas(false);
      }

      // Load billing companies
      setLoadingBillingCompanies(true);
      try {
        const perPage = prefilledData?.billingCompanyId ? 100 : 20;
        const billingCompaniesResponse = await billingInvoicesService.getBillingCompanies(1, perPage);
        const billingCompaniesData = billingCompaniesResponse.data;
        if (!isMounted) return;
        
        // Handle different response structures
        let billingCompaniesArray = [];
        if (Array.isArray(billingCompaniesData)) {
          billingCompaniesArray = billingCompaniesData;
        } else if (billingCompaniesData && Array.isArray(billingCompaniesData.data)) {
          billingCompaniesArray = billingCompaniesData.data;
        } else if (billingCompaniesData && Array.isArray(billingCompaniesData.companies)) {
          billingCompaniesArray = billingCompaniesData.companies;
        }

        if (isMounted) {
          setBillingCompanies(billingCompaniesArray);
        }
      } catch (error) {
        console.error("Error loading billing companies:", error);
        if (isMounted) setBillingCompanies([]);
      } finally {
        if (isMounted) setLoadingBillingCompanies(false);
      }

      // Load companies (taxpayers)
      setLoadingCompanies(true);
      try {
        const perPage = prefilledData?.companyId ? 200 : 100; // Load more if we need to find a specific company
        const companiesResponse = await taxPayersService.fetchTaxpayers(1, perPage, "");
        const companiesData = companiesResponse.data?.data || [];
        
        // Handle different response structures
        let companiesArray = [];
        if (Array.isArray(companiesData)) {
          companiesArray = companiesData;
        } else if (companiesData && Array.isArray(companiesData.data)) {
          companiesArray = companiesData.data;
        }

        if (isMounted) {
          setCompanies(companiesArray);
        }
      } catch (error) {
        console.error("Error loading companies:", error);
        if (isMounted) setCompanies([]);
      } finally {
        if (isMounted) setLoadingCompanies(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [prefilledData]);

  // Reset prefilled flag when prefilledData changes
  useEffect(() => {
    hasPrefilledRef.current = false;
  }, [prefilledData]);

  // Prefill form fields if prefilledData is provided
  useEffect(() => {
    // Only proceed if we have prefilledData and haven't prefilled yet
    if (!prefilledData || hasPrefilledRef.current) return;
    
    // Wait for at least schemas to be loaded (we can prefill immediately if we have the objects)
    const schemasLoaded = invoiceSchemas.length > 0;
    
    if (schemasLoaded) {
      // Set invoice schema
      if (prefilledData.invoiceSchemaId) {
        const schema = invoiceSchemas.find((s) => 
          s.id === prefilledData.invoiceSchemaId || 
          s.id === parseInt(prefilledData.invoiceSchemaId) ||
          s.id.toString() === prefilledData.invoiceSchemaId.toString()
        );
        if (schema) {
          setInvoiceSchemaId({
            value: schema.id.toString(),
            label: schema.name || `Schema ${schema.id}`,
          });
        }
      }

      // Set billing company - use passed object if available, otherwise search
      if (prefilledData.billingCompanyId) {
        if (prefilledData.billingCompany) {
          // Use the company object passed from schema
          setBillingCompanyId({
            value: prefilledData.billingCompany.id.toString(),
            label: prefilledData.billingCompany.name || `Billing Company ${prefilledData.billingCompany.id}`,
          });
        } else if (billingCompanies.length > 0) {
          // Fallback: search in loaded list
          const billingCompany = billingCompanies.find((c) => 
            c.id === prefilledData.billingCompanyId || 
            c.id === parseInt(prefilledData.billingCompanyId) ||
            c.id.toString() === prefilledData.billingCompanyId.toString()
          );
          if (billingCompany) {
            setBillingCompanyId({
              value: billingCompany.id.toString(),
              label: billingCompany.name || `Billing Company ${billingCompany.id}`,
            });
          }
        }
      }

      // Set company - use passed object if available, otherwise search
      if (prefilledData.companyId) {
        if (prefilledData.company) {
          // Use the company object passed from schema
          setCompanyId({
            value: prefilledData.company.id.toString(),
            label: prefilledData.company.name || `Company ${prefilledData.company.id}`,
          });
        } else if (companies.length > 0) {
          // Fallback: search in loaded list
          const company = companies.find((c) => {
            const matches = 
              c.id === prefilledData.companyId || 
              c.id === parseInt(prefilledData.companyId) ||
              c.id.toString() === prefilledData.companyId.toString();
            return matches;
          });
          
          if (company) {
            setCompanyId({
              value: company.id.toString(),
              label: company.name || `Company ${company.id}`,
            });
          } else {
            // Last resort: set with ID only
            setCompanyId({
              value: prefilledData.companyId.toString(),
              label: `Company ${prefilledData.companyId}`,
            });
          }
        } else {
          // Set with ID only if we have the ID
          setCompanyId({
            value: prefilledData.companyId.toString(),
            label: `Company ${prefilledData.companyId}`,
          });
        }
      }

      // Set billing period type
      if (prefilledData.billingPeriodType) {
        const periodOption = BILLING_PERIOD_OPTIONS.find(
          (opt) => opt.value === prefilledData.billingPeriodType
        );
        if (periodOption) {
          setBillingPeriodType(periodOption);
        }
      }

      hasPrefilledRef.current = true;
    }
  }, [prefilledData, invoiceSchemas, billingCompanies, companies]);

  useEffect(() => {
    if (createInvoiceInfo.success) {
      toast.success("Faktura uspešno kreirana");
      dispatch(createBillingInvoiceResetState());
      history.push(`/billing-invoices/${createInvoiceInfo.data.id}`);
    }
  }, [createInvoiceInfo.success, dispatch, history]);

  useEffect(() => {
    if (createInvoiceInfo.error) {
      toast.error("Greška pri kreiranju fakture");
      dispatch(createBillingInvoiceResetState());
    }
  }, [createInvoiceInfo.error, dispatch]);

  useEffect(() => {
    if (billingPeriodType && billingPeriodStart) {
      const startDate = moment(billingPeriodStart);
      let endDate;
      const periodType = typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType;
      switch (periodType) {
        case "monthly":
          endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
          break;
        case "quarterly":
          endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
          break;
        case "semi-annual":
          endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
          break;
        case "annual":
          endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
          break;
        default:
          return;
      }
      setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
    }
  }, [billingPeriodType, billingPeriodStart]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!invoiceSchemaId) {
      toast.error("Molimo izaberite šemu fakture");
      return;
    }

    if (!billingCompanyId) {
      toast.error("Molimo izaberite fakturisku kompaniju");
      return;
    }

    if (!companyId) {
      toast.error("Molimo izaberite kompaniju");
      return;
    }

    if (!billingPeriodStart) {
      toast.error("Molimo unesite datum početka perioda");
      return;
    }

    if (!billingPeriodEnd) {
      toast.error("Molimo unesite datum završetka perioda");
      return;
    }

    const invoiceData = {
      invoiceSchemaId: typeof invoiceSchemaId === "object" ? parseInt(invoiceSchemaId.value) : parseInt(invoiceSchemaId),
      billingCompanyId: typeof billingCompanyId === "object" ? parseInt(billingCompanyId.value) : parseInt(billingCompanyId),
      companyId: typeof companyId === "object" ? parseInt(companyId.value) : parseInt(companyId),
      billingPeriodStart: moment(billingPeriodStart).startOf("day").toISOString(),
      billingPeriodEnd: moment(billingPeriodEnd).endOf("day").toISOString(),
      billingPeriodType: typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType,
    };

    dispatch(createBillingInvoice(invoiceData));
  };

  const invoiceSchemaOptions = Array.isArray(invoiceSchemas)
    ? invoiceSchemas.map((schema) => ({
        value: schema.id.toString(),
        label: schema.name || `Schema ${schema.id}`,
      }))
    : [];

  const billingCompanyOptions = Array.isArray(billingCompanies)
    ? billingCompanies.map((company) => ({
        value: company.id.toString(),
        label: company.name || `Billing Company ${company.id}`,
      }))
    : [];

  const companyOptions = Array.isArray(companies)
    ? companies.map((company) => ({
        value: company.id.toString(),
        label: company.name || `Company ${company.id}`,
      }))
    : [];

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Nova faktura</h1>
          <button className={styles.backButton} onClick={() => history.goBack()}>
            ← Nazad
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Osnovne informacije</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Šema fakture *</label>
                {loadingSchemas ? (
                  <p className={styles.loadingText}>Učitavanje...</p>
                ) : invoiceSchemaOptions.length > 0 ? (
                  <Select
                    options={invoiceSchemaOptions}
                    value={invoiceSchemaId}
                    onChange={setInvoiceSchemaId}
                    placeholder="Izaberite šemu fakture"
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        width: "100%",
                      }),
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        "&:hover": {
                          borderColor: "#007bff",
                        },
                      }),
                    }}
                  />
                ) : (
                  <Input
                    type="number"
                    placeholder="ID šeme fakture"
                    value={invoiceSchemaId?.value || invoiceSchemaId || ""}
                    onChange={(value) => setInvoiceSchemaId(value)}
                  />
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Fakturiska kompanija *</label>
                {loadingBillingCompanies ? (
                  <p className={styles.loadingText}>Učitavanje...</p>
                ) : billingCompanyOptions.length > 0 ? (
                  <Select
                    options={billingCompanyOptions}
                    value={billingCompanyId}
                    onChange={setBillingCompanyId}
                    placeholder="Izaberite fakturisku kompaniju"
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        width: "100%",
                      }),
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        "&:hover": {
                          borderColor: "#007bff",
                        },
                      }),
                    }}
                  />
                ) : (
                  <Input
                    type="number"
                    placeholder="ID fakturiske kompanije"
                    value={billingCompanyId?.value || billingCompanyId || ""}
                    onChange={(value) => setBillingCompanyId(value)}
                  />
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Kompanija *</label>
                {loadingCompanies ? (
                  <p className={styles.loadingText}>Učitavanje...</p>
                ) : companyOptions.length > 0 ? (
                  <Select
                    options={companyOptions}
                    value={companyId}
                    onChange={setCompanyId}
                    placeholder="Izaberite kompaniju"
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        width: "100%",
                      }),
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        "&:hover": {
                          borderColor: "#007bff",
                        },
                      }),
                    }}
                  />
                ) : (
                  <Input
                    type="number"
                    placeholder="ID kompanije"
                    value={companyId?.value || companyId || ""}
                    onChange={(value) => setCompanyId(value)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Period fakturisanja</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tip perioda *</label>
                <Select
                  options={BILLING_PERIOD_OPTIONS}
                  value={billingPeriodType}
                  onChange={(selectedOption) => {
                    setBillingPeriodType(selectedOption);
                    if (billingPeriodStart) {
                      const startDate = moment(billingPeriodStart);
                      let endDate;
                      switch (selectedOption.value) {
                        case "monthly":
                          endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
                          break;
                        case "quarterly":
                          endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
                          break;
                        case "semi-annual":
                          endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
                          break;
                        case "annual":
                          endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
                          break;
                        default:
                          return;
                      }
                      setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
                    }
                  }}
                  placeholder="Izaberite tip perioda"
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: "100%",
                    }),
                    control: (provided) => ({
                      ...provided,
                      minHeight: "50px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      "&:hover": {
                        borderColor: "#007bff",
                      },
                    }),
                  }}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Datum početka *</label>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={billingPeriodStart}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBillingPeriodStart(value);
                    if (billingPeriodType && value) {
                      const startDate = moment(value);
                      let endDate;
                      const periodType = typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType;
                      switch (periodType) {
                        case "monthly":
                          endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
                          break;
                        case "quarterly":
                          endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
                          break;
                        case "semi-annual":
                          endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
                          break;
                        case "annual":
                          endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
                          break;
                        default:
                          return;
                      }
                      setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
                    }
                  }}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Datum završetka *</label>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={billingPeriodEnd}
                  onChange={(e) => setBillingPeriodEnd(e.target.value)}
                  min={billingPeriodStart}
                />
                {billingPeriodStart && billingPeriodType && (
                  <p className={styles.helperText}>
                    Automatski izračunato na osnovu tipa perioda
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className={styles.formActions}>
            <Button
              name="Otkaži"
              onClick={(e) => {
                e.preventDefault();
                history.goBack();
              }}
            />
            <Button
              name={createInvoiceInfo.attempt ? "Kreiranje..." : "Kreiraj fakturu"}
              onClick={handleSubmit}
              disabled={createInvoiceInfo.attempt}
              showLoader={createInvoiceInfo.attempt}
            />
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CreateBillingInvoiceScreen;
